import { Box } from '@mui/material';
import { AppTable } from '@/UI/AppTable/AppTable';
import { API_KEY_PROVIDERS, type ApiKeyProviderId } from './apiKeyProviders';
import { ApiKeysProviderRow } from './ApiKeysProviderRow';
import { ApiKeysTableHeader } from './ApiKeysTableHeader';
import type { MutableRefObject } from 'react';
import type { ServerRow, GoogleServiceAccountDraft } from './apiKeysTable.types';

const COLUMN_WIDTHS: Array<string | number | undefined> = ['22%', undefined, 80, 132];

export type ApiKeysTableProps = {
  serverConfigs: Partial<Record<ApiKeyProviderId, ServerRow>>;
  editingByProvider: Partial<Record<ApiKeyProviderId, boolean>>;
  draftByProvider: Partial<Record<ApiKeyProviderId, string | GoogleServiceAccountDraft>>;
  pendingEnabledByProvider: Partial<Record<ApiKeyProviderId, boolean>>;
  savingProviderId: ApiKeyProviderId | null;
  inputRefs: MutableRefObject<Partial<Record<ApiKeyProviderId, HTMLInputElement | null>>>;
  onDraftKeyChange: (providerId: ApiKeyProviderId, value: string) => void;
  onDraftFieldChange: (providerId: ApiKeyProviderId, field: keyof GoogleServiceAccountDraft, value: string) => void;
  onSave: (providerId: ApiKeyProviderId) => void;
  onDeleteKey: (providerId: ApiKeyProviderId) => void;
  onStartAdd: (providerId: ApiKeyProviderId) => void;
  onStartEdit: (providerId: ApiKeyProviderId) => void;
  onCancelEdit: (providerId: ApiKeyProviderId) => void;
  onStatusChange: (providerId: ApiKeyProviderId, nextOn: boolean) => void;
};

export const ApiKeysTable = ({
  serverConfigs,
  editingByProvider,
  draftByProvider,
  pendingEnabledByProvider,
  savingProviderId,
  inputRefs,
  onDraftKeyChange,
  onDraftFieldChange,
  onSave,
  onDeleteKey,
  onStartAdd,
  onStartEdit,
  onCancelEdit,
  onStatusChange,
}: ApiKeysTableProps) => (
  <Box sx={{ width: '100%', px: 1, mx: -1 }}>
    <AppTable columnWidths={COLUMN_WIDTHS}>
      <AppTable.Thead>
        <ApiKeysTableHeader />
      </AppTable.Thead>
      <AppTable.Tbody>
        {API_KEY_PROVIDERS.map((provider) => {
          const emptyDraft: string | GoogleServiceAccountDraft =
            provider.credentialType === 'serviceAccount' ? { clientEmail: '', projectId: '', privateKey: '' } : '';
          return (
            <ApiKeysProviderRow
              key={provider.id}
              provider={provider}
              row={serverConfigs[provider.id]}
              editing={editingByProvider[provider.id] ?? false}
              draft={draftByProvider[provider.id] ?? emptyDraft}
              pendingEnabled={pendingEnabledByProvider[provider.id] ?? true}
              isBusy={savingProviderId === provider.id}
              onDraftChange={onDraftKeyChange}
              onDraftFieldChange={onDraftFieldChange}
              onSave={onSave}
              onDelete={onDeleteKey}
              onStartAdd={onStartAdd}
              onStartEdit={onStartEdit}
              onCancelEdit={onCancelEdit}
              onStatusChange={onStatusChange}
              inputRefs={inputRefs}
            />
          );
        })}
      </AppTable.Tbody>
    </AppTable>
  </Box>
);
